import { Injectable } from '@angular/core';
import { AspectRationFormatTree, AssetUIModel, ClientsBrandsTree, StepperGroupModel, StepperModel } from '@thehubz/client/core';
import { Collection, UserAgencyClientModel } from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';

@Injectable({
  providedIn: 'root',
})
export class AssetStepperDialogService {
  /**
   * Manage visited/selected state of each steps
   * @param initialStepper StepperModel
   * @param index number
   */
  resetAllSteps(initialStepper: StepperModel, index: number) {
    initialStepper.stepper.forEach((element: StepperGroupModel, i: number) => {
      element['selected'] = false;
      if (i >= index) {
        element['visited'] = false;
      } else {
        element['visited'] = true;
      }
    });
    initialStepper.stepper[index]['selected'] = true;
    initialStepper.stepper[index]['visited'] = true;
  }

  /**
   * Creates and returns an empty filter object with ownerID set to currentUserID
   * @returns Filter object
   */
  getFilter(currentUserID: string) {
    const filterObject = {
      filters: {
        assetId: '',
        ownerID: currentUserID,
      },
      sort: { updatedAt: -1 },
    };

    return filterObject;
  }

  /**
   * Populate covers for the collection, from assets in the collection
   * @param initialStepper Stepper
   * @param collationList Collections list
   */
  setOption(initialStepper: StepperModel, collationList: Collection[]) {
    if (
      initialStepper &&
      initialStepper.collectiveStepper &&
      initialStepper.collectiveStepper[1].stepper &&
      initialStepper.collectiveStepper[1].stepper[0].formFields
    ) {
      initialStepper.collectiveStepper[1].stepper[0].formFields[0].options = collationList.map((collection: Collection) => {
        return {
          id: collection._id,
          name: collection.name,
          imageUrl: collection.assetData ? collection.assetData.previewUrl : './assets/placeholder-collection-small.png',
        };
      });
    }
  }

  /**
   * Get asset ids from collections
   * @param collections
   * @returns
   */
  getAssetIDsFromCollections(collections: Collection[]): string[] {
    return collections
      .map((collection: any) => collection.coverAssetID && (collection.coverAssetID as unknown as AssetUIModel)._id)
      .filter((assetID) => !!assetID);
  }

  /**
   * Update collections' asssetData with cover image of that collection
   * @param assets
   * @param collections
   */
  updateCollectionsAssets(assets: AssetUIModel[], collections: Collection[]) {
    collections.forEach((collection: Collection) => {
      const assetData = assets.find(
        // coverAssetID here would contain an Asset
        (asset: AssetUIModel) => (collection.coverAssetID as unknown as AssetUIModel)?._id == asset._id,
      );

      if (assetData) {
        collection.assetData = {
          previewUrl: assetData.imageUrl,
          mainFile: assetData.mainFile,
          mainUrl: assetData.mainUrl,
        } as AnyType;
      }
    });
  }

  /**
   * Get aspect details
   * @param aspectRatioFormatTree
   * @returns
   */
  getAspectParentDetails(aspectRatioFormatTree: AspectRationFormatTree[]) {
    let aspectParentDetails = {};

    aspectRatioFormatTree.forEach((element) => {
      aspectParentDetails = {
        name: element.parent?.name,
        aspectRatio: element.parent?.aspectRatio,
        aspectRatioName: element.parent?.aspectRatioName,
        defaultFileFormat: element.parent?.defaultFileFormat,
      };
    });

    return aspectParentDetails;
  }

  /**
   * Get formats for each version of asset
   * @param aspectRatioFormatTree
   * @returns
   */
  getFormats(aspectRatioFormatTree: AspectRationFormatTree[]) {
    return aspectRatioFormatTree.map((formatTree: AnyType) => {
      return {
        id: formatTree.id,
        ext: formatTree.ext,
        fallbackImg: formatTree.fallbackImg,
        fileNameRef: formatTree.fileNameRef,
        mimetype: formatTree.mimetype,
        name: formatTree.tempName,
        size: formatTree.size,
      };
    });
  }

  /**
   * Check if form have any updated value
   * @param form
   * @returns
   */
  getFormDirtyValues(form: AnyType) {
    const dirtyValues: AnyType = {};
    Object.keys(form.controls).forEach((key) => {
      const currentControl = form.controls[key];
      if (currentControl.dirty) {
        if (currentControl.controls) dirtyValues[key] = this.getFormDirtyValues(currentControl);
        else dirtyValues[key] = currentControl.value;
      }
    });
    return dirtyValues;
  }

  /**
   * Check if form have any updated value
   * @param form
   * @returns
   */
  getFormDirtyValuesEdits(form: AnyType, domainsMatchingFlag?: boolean) {
    const dirtyValues: AnyType = {};
    Object.keys(form.controls).forEach((key) => {
      const currentControl = form.controls[key];
      if (currentControl.dirty && currentControl.touched) {
        if (currentControl.controls && key !== 'productionDetails') {
          if (Array.isArray(currentControl.value) && currentControl.value.length > 0) {
            dirtyValues[key] = currentControl.getRawValue();
          } else {
            if (key === 'domains') {
              if (domainsMatchingFlag) {
                dirtyValues[key] = currentControl.getRawValue();
              } else {
                delete dirtyValues[key];
              }
            } else {
              dirtyValues[key] = this.getFormDirtyValuesEdits(currentControl, domainsMatchingFlag);
            }
          }
        } else {
          dirtyValues[key] = currentControl.getRawValue();
        }
      }
    });
    return dirtyValues;
  }

  /**
   * Transform client model to mat-tree child-parent model
   * @param publishedClients
   * @returns
   */
  transformClients = (publishedClients: UserAgencyClientModel[], originalClientList?: UserAgencyClientModel[], editable?: boolean): ClientsBrandsTree[] => {
    const treeData: ClientsBrandsTree[] = [
      {
        name: $localize`Select all clients`,
        indeterminate: false,
        selected: false,
        children: [],
      },
    ];
    if (editable && originalClientList?.length) {
      treeData[0].indeterminate = true;
    }
    if (originalClientList?.length) {
      const children = originalClientList.map((company) => {
        const cmp = publishedClients.find((client) => client._id === company._id);
        const brandArr = company.brands?.map((brnd) => {
          const brn = cmp?.brands?.find((brndd) => brndd._id === brnd._id);
          return {
            name: brnd.name,
            id: brnd._id,
            selected: brn ? true : false,
          };
        });
        return {
          name: company.name,
          id: company._id,
          selected: brandArr?.length === cmp?.brands?.length,
          indeterminate: cmp ? true : false,
          children: [
            {
              name: $localize`Select all brands`,
              children: brandArr,
              selected: brandArr?.length === cmp?.brands?.length,
              indeterminate: cmp ? true : false,
            },
          ],
        };
      });
      const isAllClientSelected = originalClientList.every((agency) => {
        const cmp = publishedClients?.find((client) => agency._id === client._id);
        return cmp?.brands?.length === agency.brands?.length;
      });
      if (publishedClients.length === originalClientList.length && isAllClientSelected) {
        treeData[0].selected = true;
      }
      treeData[0].children = children;
    } else if (publishedClients && Array.isArray(publishedClients)) {
      const children = publishedClients.map((client) => {
        const brandArr = client.brands?.map((brnd) => {
          return {
            name: brnd.name,
            id: brnd._id,
          };
        });
        return {
          name: client.name,
          id: client._id,
          children: [
            {
              name: $localize`Select all brands`,
              children: brandArr,
            },
          ],
        };
      });
      treeData[0].children = children;
    }
    return treeData;
  };
}
